import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import { displayProjectType, jobLine } from '../../utils/nomenclature';

const ACCENT = '#0f766e';
const DEFAULT_LOGO = '/lgl-logo-cropped.png';

const emptyForm = {
  legal_name: '',
  address_line1: '',
  address_line2: '',
  phone: '',
  email: '',
  website: '',
  notes: '',
};

const LetterheadPreview = ({ form, logoSrc, sample }) => (
  <div style={{
    border: '1px solid #d1d5db',
    background: '#fff',
    padding: '14px 16px 12px',
    fontFamily: 'Georgia, Times, serif',
    color: '#111',
  }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <img src={logoSrc || DEFAULT_LOGO} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} />
      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
        <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'Helvetica, Arial, sans-serif' }}>
          {form.legal_name || 'Legal name'}
        </div>
        {form.address_line1 && <div>{form.address_line1}</div>}
        {form.address_line2 && <div>{form.address_line2}</div>}
        {(form.phone || form.email) && <div>{[form.phone, form.email].filter(Boolean).join('   ')}</div>}
      </div>
    </div>
    <div style={{ marginTop: 10, fontFamily: 'Helvetica, Arial, sans-serif' }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>{sample.jobLine}</div>
      <div style={{ fontSize: 12, marginTop: 2 }}>{sample.projectType}</div>
    </div>
    <div style={{ borderTop: '1px solid #222', marginTop: 8 }} />
  </div>
);

const LetterheadSettingsModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const api = useCallback(
    (method, url, data, config) => authenticatedApiCall(instance, accounts, method, url, data, config),
    [instance, accounts]
  );

  const [letterheads, setLetterheads] = useState([]);
  const [prefixes, setPrefixes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [logoSrc, setLogoSrc] = useState(DEFAULT_LOGO);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [newPrefix, setNewPrefix] = useState({ prefix: '', division_id: '', notes: '' });

  const selected = useMemo(
    () => letterheads.find((d) => String(d.division_id) === String(selectedId)),
    [letterheads, selectedId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api('GET', '/letterhead/admin');
      const data = res.data?.data || {};
      const list = data.letterheads || [];
      setLetterheads(list);
      setPrefixes(data.prefixes || []);
      if (list.length && !selectedId) setSelectedId(list[0].division_id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load letterheads');
    } finally {
      setLoading(false);
    }
  }, [api, selectedId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!selected) return;
    setForm({
      legal_name: selected.legal_name || selected.division_name || '',
      address_line1: selected.address_line1 || '',
      address_line2: selected.address_line2 || '',
      phone: selected.phone || '',
      email: selected.email || '',
      website: selected.website || '',
      notes: selected.notes || '',
    });
    setLogoSrc(selected.logo_data_url || DEFAULT_LOGO);
  }, [selected]);

  const sample = {
    jobLine: jobLine({ job: '61075', client: 'Wieland', jobName: 'Copper Tube' }),
    projectType: displayProjectType('Lump Sum'),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError('');
    try {
      await api('PUT', `/letterhead/admin/${selectedId}`, form);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save letterhead');
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedId) return;
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api('POST', `/letterhead/admin/${selectedId}/logo`, fd);
      const next = res.data?.data;
      if (next?.logo_data_url) setLogoSrc(next.logo_data_url);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setSaving(false);
    }
  };

  const savePrefix = async (row) => {
    setSaving(true);
    setError('');
    try {
      await api('PUT', `/letterhead/admin/prefixes/${row.id}`, {
        prefix: row.prefix,
        division_id: row.division_id,
        confirmed: row.confirmed,
        notes: row.notes,
        variant: row.variant,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save prefix rule');
    } finally {
      setSaving(false);
    }
  };

  const addPrefix = async () => {
    if (!newPrefix.prefix.trim() || !newPrefix.division_id) return;
    setSaving(true);
    setError('');
    try {
      await api('POST', '/letterhead/admin/prefixes', {
        ...newPrefix,
        confirmed: false,
      });
      setNewPrefix({ prefix: '', division_id: '', notes: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add prefix rule');
    } finally {
      setSaving(false);
    }
  };

  const deletePrefix = async (id) => {
    if (!window.confirm('Delete this prefix rule?')) return;
    setSaving(true);
    try {
      await api('DELETE', `/letterhead/admin/prefixes/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete prefix rule');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const input = {
    width: '100%',
    padding: '0.45rem 0.6rem',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: '0.85rem',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 980, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative' }}>
        <div style={{ padding: '16px 20px', background: ACCENT, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>Letterhead</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.9 }}>One legal-entity header per division. Job-number prefix picks the company on PDFs.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setShowHelp(true)} style={{ background: 'rgba(255,255,255,0.18)', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>❓ Help</button>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: 16, overflowY: 'auto' }}>
          {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: '8px 10px', borderRadius: 6, marginBottom: 12 }}>{error}</div>}
          {loading && <p style={{ color: '#64748b' }}>Loading…</p>}

          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16 }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              {letterheads.map((d) => (
                <button
                  key={d.division_id}
                  onClick={() => setSelectedId(d.division_id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 'none',
                    borderBottom: '1px solid #f1f5f9',
                    background: String(d.division_id) === String(selectedId) ? '#ccfbf1' : 'white',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{d.division_number} · {d.division_name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{d.legal_name || 'No letterhead yet'}</div>
                </button>
              ))}
            </div>

            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  {['legal_name', 'address_line1', 'address_line2', 'phone', 'email', 'website'].map((name) => (
                    <label key={name} style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#334155' }}>
                      {name.replace(/_/g, ' ')}
                      <input name={name} value={form[name]} onChange={handleChange} style={{ ...input, marginTop: 4 }} />
                    </label>
                  ))}
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#334155' }}>
                    notes
                    <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} style={{ ...input, marginTop: 4 }} />
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    <label style={{ ...input, width: 'auto', cursor: 'pointer', background: '#f8fafc' }}>
                      Upload logo
                      <input type="file" accept="image/*" onChange={handleLogo} style={{ display: 'none' }} />
                    </label>
                    <button onClick={handleSave} disabled={saving} style={{ padding: '8px 14px', background: ACCENT, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                      {saving ? 'Saving…' : 'Save letterhead'}
                    </button>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Live preview</div>
                  <LetterheadPreview form={form} logoSrc={logoSrc} sample={sample} />
                </div>
              </div>
            </div>
          </div>

          <h4 style={{ margin: '22px 0 8px' }}>Job-number prefix rules</h4>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b' }}>
            First matching prefix wins (longest first). Leading T is stripped after an exact match is tried. Rows stay unconfirmed until TJ signs off.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: 6 }}>Prefix</th>
                <th style={{ padding: 6 }}>Division</th>
                <th style={{ padding: 6 }}>Confirmed</th>
                <th style={{ padding: 6 }}>Notes</th>
                <th style={{ padding: 6 }} />
              </tr>
            </thead>
            <tbody>
              {prefixes.map((row) => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: 6 }}>
                    <input value={row.prefix} onChange={(e) => setPrefixes((p) => p.map((r) => r.id === row.id ? { ...r, prefix: e.target.value } : r))} style={{ ...input, width: 80 }} />
                  </td>
                  <td style={{ padding: 6 }}>
                    <select value={row.division_id} onChange={(e) => setPrefixes((p) => p.map((r) => r.id === row.id ? { ...r, division_id: e.target.value } : r))} style={input}>
                      {letterheads.map((d) => (
                        <option key={d.division_id} value={d.division_id}>{d.division_number} · {d.division_name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 6 }}>
                    <input type="checkbox" checked={!!row.confirmed} onChange={(e) => setPrefixes((p) => p.map((r) => r.id === row.id ? { ...r, confirmed: e.target.checked } : r))} />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input value={row.notes || ''} onChange={(e) => setPrefixes((p) => p.map((r) => r.id === row.id ? { ...r, notes: e.target.value } : r))} style={input} />
                  </td>
                  <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                    <button onClick={() => savePrefix(row)} style={{ marginRight: 6, cursor: 'pointer' }}>Save</button>
                    <button onClick={() => deletePrefix(row.id)} style={{ cursor: 'pointer', color: '#b91c1c' }}>Delete</button>
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: '1px solid #e5e7eb', background: '#f8fafc' }}>
                <td style={{ padding: 6 }}>
                  <input placeholder="61" value={newPrefix.prefix} onChange={(e) => setNewPrefix((p) => ({ ...p, prefix: e.target.value }))} style={{ ...input, width: 80 }} />
                </td>
                <td style={{ padding: 6 }}>
                  <select value={newPrefix.division_id} onChange={(e) => setNewPrefix((p) => ({ ...p, division_id: e.target.value }))} style={input}>
                    <option value="">Division…</option>
                    {letterheads.map((d) => (
                      <option key={d.division_id} value={d.division_id}>{d.division_number} · {d.division_name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: 6, color: '#94a3b8' }}>—</td>
                <td style={{ padding: 6 }}>
                  <input placeholder="Notes" value={newPrefix.notes} onChange={(e) => setNewPrefix((p) => ({ ...p, notes: e.target.value }))} style={input} />
                </td>
                <td style={{ padding: 6 }}>
                  <button onClick={addPrefix} disabled={saving} style={{ cursor: 'pointer' }}>Add</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {showHelp && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <div style={{ background: 'white', borderRadius: 10, width: 620, maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 15px 40px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '0.8rem 1.1rem', background: ACCENT, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '10px 10px 0 0' }}>
                <h4 style={{ margin: 0 }}>❓ Letterhead Help</h4>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <ListenButton compact guideId="letterhead-settings-help" title="Letterhead Help" accent={ACCENT} dark />
                  <button onClick={() => setShowHelp(false)} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>✕</button>
                </div>
              </div>
              <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', fontSize: '0.85rem', color: '#334155', lineHeight: 1.55 }}>
                <p><strong>What a division is.</strong> A division is a legal entity (Ltd Canada, LGL Inc, Tanks, Quebec, and so on) — not a trade. Each one has its own letterhead: legal name, address, phone, and logo.</p>
                <p><strong>How a PDF picks a company.</strong> The portal looks at the job number first and uses the longest matching prefix in the table below. If nothing matches, it uses the project’s division. If that is also blank, it falls back to Ltd Canada.</p>
                <p><strong>Project type.</strong> The second line under the job is the project type (Lump Sum, T&amp;M, …). If a job has no type yet, PDFs print <code>...</code> — existing jobs stay blank until someone edits them on Project Details.</p>
                <p><strong>Temp / T jobs.</strong> A leading T is tried as-is (so T63 can be its own rule), then stripped so T61075 still follows the 61 rule.</p>
                <p><strong>Questions for TJ</strong> (paste answers later; flip Confirmed on each row):</p>
                <ul style={{ paddingLeft: 18 }}>
                  <li>Confirm series 75, 63 / T63, and any others (20, 50, 80, Quebec).</li>
                  <li>Which job numbers belong to MRL?</li>
                  <li>Is the official project-type list Lump Sum / T&amp;M / Unit Price / Cost Plus?</li>
                  <li>Preferred SharePoint job-site title if it should differ from <code>{'{job}{disc} {Name}'}</code>.</li>
                  <li>Is “Ltd US w Florida” ever needed as its own prefix? Today 30 uses Syracuse-only Ltd US and 61 uses LGL Inc (Bartow + Syracuse).</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LetterheadSettingsModal;
